import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Badge,
  FormGroup
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import { authAPI, usersAPI } from "../../services/api";
import { toast } from "react-toastify";

const UserProfile = (props) => {
  document.title = "Profil | Rakops Solutions";

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États pour les documents
  const [documents, setDocuments] = useState([]);
  const [showDocForm, setShowDocForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docFormData, setDocFormData] = useState({
    nom: "",
    type: "cv",
    fichier: null
  });

  useEffect(() => {
    props.setBreadcrumbItems("Profil Utilisateur", [
      { title: "Main", link: "#" },
      { title: "Profil", link: "#" },
    ]);
    fetchUserData();
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchUserDocuments();
    }
  }, [user]);

  const fetchUserDocuments = async () => {
    try {
      const response = await usersAPI.getDocuments(user.id);
      setDocuments(response.data);
    } catch (err) {
      console.error("Erreur chargement documents", err);
    }
  };

  const handleUploadDocument = async () => {
    if (!docFormData.fichier || !docFormData.nom) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("fichier", docFormData.fichier);
      formData.append("type", docFormData.type);
      formData.append("nom", docFormData.nom);

      await usersAPI.uploadDocument(user.id, formData);
      toast.success("Document uploadé avec succès !");
      setDocFormData({ nom: "", type: "cv", fichier: null });
      setShowDocForm(false);
      fetchUserDocuments();
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (window.confirm("Voulez-vous vraiment supprimer ce document ?")) {
      try {
        await usersAPI.deleteDocument(user.id, docId);
        toast.success("Document supprimé");
        fetchUserDocuments();
      } catch (err) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getMe();
      if (response.data.success) {
        setUser(response.data.user);
        // Mettre à jour le localStorage au cas où les infos auraient changé
        localStorage.setItem('authUser', JSON.stringify(response.data.user));
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du profil", err);
      setError("Impossible de charger votre profil.");
    } finally {
      setLoading(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || "",
      telephone: user?.telephone || "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Le nom est obligatoire"),
      telephone: Yup.string().nullable(),
      password: Yup.string().min(8, "Le mot de passe doit faire au moins 8 caractères").nullable(),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
        .when('password', {
          is: (val) => val && val.length > 0,
          then: (schema) => schema.required('Veuillez confirmer votre mot de passe')
        })
    }),
    onSubmit: async (values) => {
      try {
        const payload = { ...values };
        if (!payload.password) {
          delete payload.password;
          delete payload.password_confirmation;
        }

        const response = await authAPI.updateProfile(payload);
        if (response.data.success) {
          toast.success("Profil mis à jour avec succès !");
          setUser(response.data.user);
          localStorage.setItem('authUser', JSON.stringify(response.data.user));
          validation.resetForm({ values: { ...values, password: "", password_confirmation: "" } });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Erreur lors de la mise à jour");
      }
    },
  });

  if (loading) return <div className="p-5 text-center">Chargement...</div>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg="4">
              <Card className="directory-card">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-shrink-0 me-3">
                      <div className="avatar-lg">
                        <span className="avatar-title rounded-circle bg-primary font-size-24">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="text-primary font-size-18 mb-1">{user?.name}</h5>
                      <p className="font-size-12 mb-2 text-muted">{user?.email}</p>
                      <Badge color="info" className="font-size-11">
                        {user?.role?.toUpperCase()}
                      </Badge>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="mt-3">
                    <p className="text-muted mb-1">Téléphone</p>
                    <h6>{user?.telephone || "Non renseigné"}</h6>
                  </div>
                  <div className="mt-3">
                    <p className="text-muted mb-1">Équipe</p>
                    <h6>{user?.team?.nom || "Aucune équipe"}</h6>
                  </div>
                  <div className="mt-3">
                    <p className="text-muted mb-1">Membre depuis</p>
                    <h6>{new Date(user?.created_at).toLocaleDateString()}</h6>
                  </div>
                </CardBody>
              </Card>

              <Card className="mt-4">
                <CardBody>
                  <h5 className="card-title mb-4">Statistiques Activité</h5>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Tickets Assignés</span>
                    <Badge color="primary" pill>{user?.tickets_assignes_count || 0}</Badge>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Projets Gérés</span>
                    <Badge color="info" pill>{user?.projets_geres_count || 0}</Badge>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg="8">
              <Card>
                <CardBody>
                  <h5 className="card-title mb-4">Paramètres du Profil</h5>
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Nom Complet</Label>
                          <Input
                            name="name"
                            className="form-control"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.name}
                            invalid={validation.touched.name && !!validation.errors.name}
                          />
                          <FormFeedback>{validation.errors.name}</FormFeedback>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Téléphone</Label>
                          <Input
                            name="telephone"
                            className="form-control"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.telephone}
                            invalid={validation.touched.telephone && !!validation.errors.telephone}
                          />
                          <FormFeedback>{validation.errors.telephone}</FormFeedback>
                        </div>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <h5 className="font-size-14 mb-3">Sécurité (Laisser vide pour ne pas changer)</h5>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Nouveau mot de passe</Label>
                          <Input
                            name="password"
                            type="password"
                            className="form-control"
                            placeholder="Min. 8 caractères"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={validation.touched.password && !!validation.errors.password}
                          />
                          <FormFeedback>{validation.errors.password}</FormFeedback>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Confirmer le mot de passe</Label>
                          <Input
                            name="password_confirmation"
                            type="password"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password_confirmation}
                            invalid={validation.touched.password_confirmation && !!validation.errors.password_confirmation}
                          />
                          <FormFeedback>{validation.errors.password_confirmation}</FormFeedback>
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-4 text-end">
                      <Button type="submit" color="primary" className="w-md waves-effect waves-light">
                        Enregistrer les modifications
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>

              {/* SECTION DOCUMENTS PERSONNELS */}
              <Card className="mt-4">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title">Mes Documents (CV, Contrats, etc.)</h5>
                    <Button color="success" size="sm" onClick={() => setShowDocForm(!showDocForm)}>
                      <i className={`mdi mdi-${showDocForm ? 'minus' : 'plus'} me-1`}></i>
                      {showDocForm ? 'Annuler' : 'Ajouter un document'}
                    </Button>
                  </div>

                  {showDocForm && (
                    <div className="p-3 bg-light rounded mb-4">
                      <Row className="align-items-end">
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label>Nom du document</Label>
                            <Input
                              type="text"
                              value={docFormData.nom}
                              onChange={(e) => setDocFormData({ ...docFormData, nom: e.target.value })}
                              placeholder="ex: CV 2024"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup className="mb-0">
                            <Label>Type de document</Label>
                            <Input
                              type="select"
                              value={docFormData.type}
                              onChange={(e) => setDocFormData({ ...docFormData, type: e.target.value })}
                            >
                              <option value="demande_stage">Demande de stage</option>
                              <option value="contrat">Contrat</option>
                              <option value="attestation_travail">Attestation de travail</option>
                              <option value="attestation_stage">Attestation de stage</option>
                              <option value="cv">CV</option>
                              <option value="contrat_confidentialite">Contrat de confidentialité</option>
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup className="mb-0">
                            <Label>Fichier</Label>
                            <Input
                              type="file"
                              onChange={(e) => setDocFormData({ ...docFormData, fichier: e.target.files[0] })}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={2}>
                          <Button color="primary" className="w-100" onClick={handleUploadDocument} disabled={uploading}>
                            {uploading ? "Envoi..." : "Uploader"}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="table table-centered table-nowrap mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Nom</th>
                          <th>Type</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.length > 0 ? documents.map((doc) => (
                          <tr key={doc.id}>
                            <td>
                              <i className="mdi mdi-file-document-outline text-primary me-2 font-size-18"></i>
                              {doc.nom}
                            </td>
                            <td>
                              <Badge color="light" className="text-secondary text-uppercase">{doc.type}</Badge>
                            </td>
                            <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info">
                                  <i className="mdi mdi-download"></i>
                                </a>
                                <Button color="danger" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                                  <i className="mdi mdi-trash-can"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="4" className="text-center p-4 text-muted">
                              Aucun document disponible
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(UserProfile);
